import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './SearchBar.css';
import Button from '../Button.js';
import ButtonGroup from '../ButtonGroup.js';
import ImageButton from '../ImageButton.js';
import DataElement from '../DataElement.js';
import PopupBox from '../PopupBox.js';
import ContentEditable from 'react-contenteditable';
import TextInput from '../TextInput.js';
import searchBeginsWith from '../../images/search-begins-with.png';
import searchEndsWith from '../../images/search-ends-with.png';
import searchContains from '../../images/search-contains.png';
import searchCancel from '../../images/search-cancel.png';
import CGSearch from './CGSearch.js';
import SearchBarSettings from './SearchBarSettings.js';

// TODO:
// - TokenSelected (Fix so empty token reamins when popup is open )
//   - Remove from state
//   - Instead add property to token (ie token.selected = true)
//
// - FUTURE
// - This searchbar can go into any job data card
// - To prevent fullwidth single buttons at the bottom, we can add a blank div to fill that space
// - SHould we check path name length? And if greater than some length have it be a signle button?
// - NOTE: there is a link to "Table Search" in the help section, but it does not exist yet.
// - When we have the ability to add features to track directly from a job results:
//   - Add source id automatically as search term!
// - We can have a VALUES section right under search term
//   - string: list of first (upto max) possible values for the attribute
//   - eventually, we can allow selection of mulitple values somehow
//     - I think the format would be: [value1,value2,"value 3"]:someColumn

// Map of modifiers to text or image to show in buttons
const modifierMap = {
  eq: '=',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
  begins:   <img src={searchBeginsWith} alt='Starts With' />,
  ends:     <img src={searchEndsWith} alt='Ends With' />,
  contains: <img src={searchContains} alt='Contains' />,
};

const stringModifiers = ['contains', 'begins', 'ends', 'eq'];
const numberModifiers = ['lt', 'lte', 'eq', 'gte', 'gt'];

// ToolTips for the buttons
const tips = {
  eq: 'Equals',
  gt: 'Greater Than',
  lt: 'Lesser Than',
  gte: 'Greater Than Equal',
  lte: 'Lesser Than Equal',
  begins: 'Begins With',
  ends:   'Ends With',
  contains: 'Contains',
  not: 'Must Not Include this Term',
  and: 'Must Include this Term',
  or: 'Must Include One of the OR Terms',
}

const typeMap = {
  'string': 'abc',
  'number': '#',
  'boolean': 'T/F',
  // 'object': '{}',
  'object': ' ',
  // We treat arrays as strings (they're values are joined by spaces)
  // 'array': '[]',
  'array': 'abc',
}

class SearchBar extends React.Component {

  static propTypes = {
    data: PropTypes.array.isRequired,
    dataName: PropTypes.string,
    onSearch: PropTypes.func.isRequired,
    initialSearch: PropTypes.string,
    // boundaryRef (if provided) will be used to constrain the search tokens setting popup
    boundaryRef: PropTypes.object,
    searchColumnPaths: PropTypes.arrayOf(PropTypes.string),
    searchExcludedColumnPaths: PropTypes.arrayOf(PropTypes.string),
    searchID: PropTypes.string,
    nameMap: PropTypes.object, // Map of column paths to names (used for display in the search bar)
  }

  static defaultProps = {
    dataName: 'Data',
    searchColumnPaths: [],
    searchExcludedColumnPaths: [],
    searchID: 'default',
    nameMap: {},
  }

  // NOTE: columnPaths are case sensitive
  constructor(props) {
    super(props);

    // NOTE: the delay could be dependent on the data/filteredData length
    // more data = longer delay
    this.searchDelay = 300;
    this.searchTimeout = 0;
    this.contentEditableRef = React.createRef();
    const columnPaths = props.searchColumnPaths;
    this.cgSearch = new CGSearch(props.data, {
      columns: columnPaths,
      excludeColumns: props.searchExcludedColumnPaths,
      determineTypes: true,
      // debug: true
    });

    this.nameMap = props.nameMap || {};

    this.state = {
      tokens: props.initialSearch ? this.cgSearch.tokenize(props.initialSearch) : [],
      tokenSelected: null,
      isFocused: false,
    };

    // this.search = this.search.bind(this);
    this.asyncSearch = this.asyncSearch.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.decorateTokens = this.decorateTokens.bind(this);
    this.clickSearchBar = this.clickSearchBar.bind(this);
    this.clickCancelSearch = this.clickCancelSearch.bind(this);
    this.generateSearchString = this.generateSearchString.bind(this);
    this.updateToken = this.updateToken.bind(this);
  }

  decorateTokens(tokens) {
    const selectedToken = this.state.tokenSelected;

    return tokens.map( (t, i) => {
      let label = 'any';
      if (t.columnType === 'unknown') {
        label = '?';
      } else if (t.columnType === 'parent') {
        label = `${t.column} any`;
      } else if (t.columnType === 'known') {
        label = t.column;
      }
      if (this.nameMap[t.column]) {
        label = this.nameMap[t.column];
      }

      function highlightValue(raw, value) {
        const index = raw.indexOf(value);
        if (index === -1) {
          // If value is not found, just return raw wrapped in a span
          return `<span>${raw}</span>`;
        }
        const before = raw.substring(0, index);
        const match = raw.substring(index, index + value.length);
        const after = raw.substring(index + value.length);
        return `<span>${before}</span><strong>${match}</strong><em style='font-size: smaller;'>${after}</em>`;
      }

      const selected = (selectedToken === t) ? 'token-selected' : '';
      return `<div class="token ${selected} ${t.logic}" data-index="${i}"><div class="token-column" contenteditable="false">${label}</div><div class="token-value">${highlightValue(t.raw, t.value)}</div></div>`;

    });
  }

  // SearchBox text has changed
  onSearchChange(event) {
    if (event.type === 'blur') return; // Prevents the search closing Token Settings
    const value = (event && event.target) ? event.target.value : '';
    this.searchWithString(value);
    // const tokens = this.tokenize(value);
    // if (tokens[0] && tokens[0].raw === '') {
    //   tokens.shift();
    // }
    // this.setState({
    //   tokens: tokens,
    //   tokenSelected: null,
    // });
    // this.asyncSearch(tokens);
  }

  asyncSearch(tokens=[]) {
    // console.log('ASYNC')
    const { onSearch } = this.props;
    if (this.searchTimeout) { clearTimeout(this.searchTimeout); }
    this.searchTimeout = setTimeout( () => {
      // const filtered = this.search(tokens);
      const filtered = this.cgSearch.search(tokens);
      onSearch(filtered);
    }, this.searchDelay);
  }

  searchWithString(string = '') {
    // console.log('Search with String', string);
    // const tokens = this.tokenize(string);
    const tokens = this.cgSearch.tokenize(string);
    if (tokens[0] && tokens[0].raw === '') {
      tokens.shift();
    }
    this.setState({
      tokens: tokens,
      tokenSelected: null,
    });
    this.asyncSearch(tokens);
  }

  generateSearchString(tokens) {
    tokens = tokens || [...this.state.tokens];
    const separatorDiv = '<div class="token-separator">&nbsp;</div>';
    let searchString;
    if (tokens.length > 0 && tokens[tokens.length - 1].raw === '') {
      tokens.pop();
      searchString = this.decorateTokens(tokens).join(separatorDiv);
      searchString += separatorDiv;
    } else {
      searchString = this.decorateTokens(tokens).join(separatorDiv);
    }
    return searchString;
  }

  // Bring up Token Settings if a token is clicked
  clickSearchBar(event) {
    const el = event.target;
    let tokenEl;
    if (el.classList.contains('token')) {
      tokenEl = el;
    } else if (el.parentNode && el.parentNode.classList.contains('token')) {
      tokenEl = el.parentNode;
    }
    if (tokenEl) {
      const { tokens } = this.state;
      const index = tokenEl.getAttribute('data-index');
      this.setState({ tokenSelected: tokens[index] })
    } else {
      this.setState({ tokenSelected: null })
    }
  }

  clickCancelSearch() {
    this.contentEditableRef.current.focus();
    this.setState({tokens: []});
    this.asyncSearch();
  }

  cancelSearchIcon() {
    if (this.state.tokens.length > 0) {
      return (
        <div className='cancel-search' onClick={this.clickCancelSearch}>
          <img src={searchCancel} alt='cancel' title='Clear Search' />
        </div>
      )
    }
  }

  // This updates the token based on changes to the token settings popup.
  // FIXME: will need to up 'value', if we add that to the popup
  updateToken(token, values={}) {
    console.log('Update Token', token, values);
    const newToken = {...token, ...values};
    // Update modifier if column changes
    if (Object.keys(values).includes('column')) {
      // Update columnType
      newToken.columnType = this.cgSearch.columnTypeFor(newToken.column);
      const columnPathType = this.cgSearch.columnPathTypes.get(newToken.column);
      const searchType = columnPathType || 'string';
      const modifier = newToken.modifier;
      if (searchType === 'string' && numberModifiers.includes(modifier)) {
        newToken.modifier = 'contains';
      }
      if (searchType === 'number' && stringModifiers.includes(modifier)) {
        newToken.modifier = 'eq';
      }
    }

    // Update RegEx
    newToken.regex = CGSearch.regexify(newToken);
    // console.log(newToken.regex)

    // Update Raw Value to include modifier/logic characters
    newToken.raw = CGSearch.rawifyToken(newToken);
    // Set the raw value to a space to keep token visible if it is empty and a provided raw of ' ' (space) is provided
    // if (newToken.raw === '' && values?.raw === ' ') {
    //   newToken.raw = ' ';
    // }
    console.log('New Token', newToken);

    // let newTokens;
    this.setState( ( {tokens} ) => { 
      const newTokens = [...tokens];
      const index = newTokens.indexOf(token);
      newTokens[index] = newToken;
      this.asyncSearch(newTokens);
      return {
        tokens: newTokens,
        tokenSelected: newToken,
      }
    });

    return newToken;
  }

  addEmptyToken() {
    const emptyToken = this.cgSearch.tokenize('new')[0];
    emptyToken.value = '';
    emptyToken.raw = ' '; // Add this sapce to make the token visible
    this.setState( ( {tokens} ) => {
      const newTokens = [...tokens, emptyToken];
      return {
        tokens: newTokens,
      }
    },    () => setTimeout(() => this.setState({ tokenSelected: emptyToken }), 10)
  );
}


  modifierButton(type, value, width, onClick, disabled=false) {
    const name = modifierMap[type] || type.toUpperCase()[0];
    return (
      <Button title={tips[type]} key={type} width={width} active={type === value} disabled={disabled} onClick={onClick}>{name}</Button>
    )
  }

  static getCurrentColumnName(path, nameMap={}) {
    // Use the nameMap if it exists
    if (nameMap[path]) {
      return nameMap[path];
    }
    // Remove trailing colon if present, but remember if it was there
    const hasTrailingColon = path.endsWith(':');
    // Split by ':', ignore empty segments (to handle "bob:meta:")
    const parts = path.split(':').filter(Boolean);
    if (parts.length === 0) return ''; // if path was just ":"
    // Get the last non-empty part
    let last = parts[parts.length - 1];
    // If path ends with ':' (group), add it back
    if (hasTrailingColon) last += ':';
    return last;
  }

  /**
   * Move all strings that end with ":" to the front, preserving order.
   * @param {string[]} arr - original array (left untouched)
   * @returns {string[]}   - new, reordered array
   */
  static colonFirst(arr) {
    return [
      ...arr.filter(s => s.endsWith(':')),   // group 1: with trailing colon
      ...arr.filter(s => !s.endsWith(':'))   // group 2: everything else
    ];
  }

  static getParentColumnName(path) {
    if (path.endsWith(':')) return path;
    const lastColonIdx = path.lastIndexOf(':');
    if (lastColonIdx === -1) return ':';
    return path.slice(0, lastColonIdx + 1);
  }

  static backButtonPath(path) {
    if (!path || path === ':') return ';';
    // Remove trailing colon(s)
    path = path.replace(/:+$/, '');
    const parts = path.split(':');
    if (parts.length <= 1) {
      return ':'; // top-level
    }
    // Remove the last segment
    parts.pop();
    return parts.join(':') + ':';
  }

  focusSearchBar() {
    if (this.contentEditableRef.current) {
      this.contentEditableRef.current.focus();
    }
  }


  validateSearchTermValue(value, columnPathType) {
    if (columnPathType === 'number' && isNaN(value) && value !== '') {
      return 'Value is not a number';
    } else if (columnPathType === 'boolean' && !['true', 'false'].includes(value.toLowerCase())) {
      return 'Value is not true or false';
    }
    return null;
  }

  tokenSettings() {
    const { tokenSelected } = this.state;
    const { boundaryRef, dataName } = this.props;
    const logicButtonWidth = 30;
    const modifierButtonWidthNumbers = 28;
    const modifierButtonWidthStrings = 35;
    // const buttonHeight = 24;

    // FIXME: updateTokens needs update
    const update = (values) => () => this.updateToken(tokenSelected, values);

    if (tokenSelected) {
      const logic = tokenSelected.logic;
      const modifier = tokenSelected.modifier;
      let modifiers;
      const columnPath = tokenSelected.column;
      const columnPathType = this.cgSearch.columnPathTypes.get(columnPath);
      const modifiersDisabled = (columnPathType === 'boolean');
      const numericSearch = columnPathType === 'number';
      const modifierTypes = numericSearch ? numberModifiers : stringModifiers;
      const modifierButtonWidth = numericSearch ? modifierButtonWidthNumbers : modifierButtonWidthStrings;
      modifiers = (
        <DataElement label='Modifiers' align='center'>
          <ButtonGroup className='modifier-group'>
            {modifierTypes.map( type => (
              this.modifierButton(type, modifier, modifierButtonWidth, update({modifier: type}), modifiersDisabled ) 
            ))}
          </ButtonGroup>
        </DataElement>
      )

      const allColumns = this.cgSearch.columnPaths
      const currentColumn = tokenSelected.column;
      let filter = SearchBar.getParentColumnName(currentColumn);
      const myColumns = SearchBar.colonFirst(CGSearch.columnPathsFilter(allColumns, filter, 1));

      // Add 1 for 'Any' Column
      // const columnRowCount = Math.ceil((myColumns.length + 1) / 2);
      // const margin = 5;
      // const labelHeight = 16;
      // const settingsHeight = (margin * 7) + (buttonHeight * 2) + (labelHeight * 3) + (margin + buttonHeight) * columnRowCount + 5 + 4;

      const tokenIndex = this.state.tokens.indexOf(tokenSelected);
      let tokenEl = this.contentEditableRef.current.getElementsByClassName("token")[tokenIndex];
      // If the token is undefined used the search box
      tokenEl = tokenEl || this.contentEditableRef.current;

      const tokenDeleteOrClose = (
        <ButtonGroup separated={true} className='token-settings-delete-close'>
          <ImageButton
            imageName='delete'
            size='small'
            title='Delete Search Term'
            onClick={ () => {
              this.setState(({ tokens, tokenSelected }) => {
                const newTokens = tokens.filter(t => t !== tokenSelected);
                return {
                  tokens: newTokens,
                  tokenSelected: null,
                }
              })
            }} />
          <ImageButton
            imageName='sidebarClose'
            size='small'
            title='Close Search Term Settings'
            onClick={ () => this.setState({ tokenSelected: null }) } />
        </ButtonGroup>
      );

      const searchTermValue = tokenSelected.value.replace(/"/g, '');

      let errorString = this.validateSearchTermValue(searchTermValue, columnPathType);

      // console.log(tokenSelected)
      return (
        <PopupBox
          // height={settingsHeight}
          height='auto'
          // style={{ maxHeight: '80vh', overflowY: 'auto' }}
          width={260}
          targetNode={tokenEl}
          boundaryNode={boundaryRef}
          dismissOnBlur={() => this.setState({ tokenSelected: null })} >
          <DataElement className='search-term-element' label='Search Term' align='center' labelRight={tokenDeleteOrClose} >
            <div>
              <TextInput value={searchTermValue}
                focusOnMount={true}
                onChange={(value) => {
                  value = value.replace(/"/g, '');
                  // const hasWhitespace = /\s/.test(value);
                  // if (hasWhitespace) {
                  //   value = `"${value}"`;
                  // }
                  // Trying to keep the token visible if empty
                  // const raw = value.trim() === '' ? ' ' : tokenSelected.raw;
                  // this.updateToken(tokenSelected, {value, raw})
                  this.updateToken(tokenSelected, {value})
                }} />
            </div>
          </DataElement>
          <SearchValues
            tokenSelected={tokenSelected}
            filter={filter}
            update={update}
            cgSearch={this.cgSearch}
          />
          { errorString && <div className='search-term-error'>{errorString}</div>}
          <div className='token-settings-header'>
            <DataElement label='Logic' align='center'>
              <ButtonGroup className='logic-group'>
                {['or', 'and', 'not'].map( value => (
                  <Button width={logicButtonWidth} key={value}
                    title={tips[value]}
                    active={logic === value}
                    onClick={ update({logic: value}) }>{value.toUpperCase()}</Button>
                ))}
              </ButtonGroup>
          </DataElement>
            {modifiers}
          </div>
          <ColumnSelector
            tokenSelected={tokenSelected}
            filter={filter}
            myColumns={myColumns}
            dataName={dataName}
            update={update}
            cgSearch={this.cgSearch}
            nameMap={this.nameMap}
          />
        </PopupBox>
      )
    }
  }

  render() {
    const { searchID } = this.props;

    const searchBarClass = classNames( 'SearchBar', this.props.className );
    const searchString = this.generateSearchString();
    return (
      <div className='SearchContainer'>
        <div className='search-row'>
          <ContentEditable
            innerRef= {this.contentEditableRef}
            spellCheck={false}
            className={searchBarClass}
            html={searchString}
            onChange={this.onSearchChange}
            onClick={this.clickSearchBar}
            onFocus={() => this.setState({ isFocused: true })}
            onBlur={() => this.setState({ isFocused: false })}
            data-placeholder='Search...' />
          {this.cancelSearchIcon()}
            <ImageButton
              className='add-token'
              imageName='add'
              title='Add New Search Term'
              onClick={() => this.addEmptyToken() } />
          {this.tokenSettings()}
        </div>
        <SearchBarSettings
          tokens={this.state.tokens}
          isFocused={this.state.isFocused}
          searchID={searchID}
          onSavedSearchSelected={(searchString) => this.searchWithString(searchString)}
          onDrawerClosed={() => this.focusSearchBar()}
        />
      </div>
    )
  }

}
export default SearchBar;


function ColumnSelector({ tokenSelected, filter, myColumns, dataName, update, cgSearch, nameMap }) {
  const columnButtonWidth = 115;

  let anyDiv = 'any';
  if (filter !== ':') {
    anyDiv = <div className='any-column'><div className='any-column-title'>any</div><div className='any-column-sub'>{SearchBar.getParentColumnName(filter)}</div></div>;
  }

  const dataTitle = (filter === ':') ? dataName : `${filter}`

  let backBtn;
  if (filter !== ':') {
    const backPath =  SearchBar.backButtonPath(filter);
    backBtn = (
      <ImageButton
        imageName='backward'
        size='small'
        title='Back to All Columns'
        onClick={ update({column: backPath}) } />
    );
  }

  if (filter.startsWith('?')) {
    return (
      <DataElement label={<div className='token-settings-title-line'>?: Unknown attribute</div>} align='center'>
        <Button width='100%'
          onClick={ update({column: ':'}) }>
            Back to All Attributes
          <div className='column-type'>?</div>
        </Button>
      </DataElement>
    );
  }

  let anyTitle = `Search Any ${dataName} attributes`
  if (filter !== ':') {
    anyTitle = `Restrict to Any '${SearchBar.getParentColumnName(filter)}' attribute`;
  }
  return (
    <DataElement className='token-settings-attributes-section' align='center'>
      <>
        <div className='token-settings-title-line'>{backBtn}<div>{`${dataTitle} attributes`}</div></div>
        <div className='token-settings-columns scroll-skinny'>
          <Button
            className='any-column-button'
            active={tokenSelected.column === filter}
            title={anyTitle}
            onClick={ update({column: filter}) }>
            {anyDiv}
            <div className='column-type'>abc</div>
          </Button>
          { myColumns.map(colPath => {
              // colPath = colPath.toLowerCase();
              const columnPathType = cgSearch.columnPathTypes.get(colPath);
              const typeString = typeMap[columnPathType] || '?';
              const btnClass = colPath.endsWith(':') ? 'column-parent' : null;
              const name = SearchBar.getCurrentColumnName(colPath, nameMap);
              const key = nameMap[colPath] ? ` (key: ${SearchBar.getCurrentColumnName(colPath)})` : '';
              return (
                <Button key={colPath} width={columnButtonWidth}
                  className={btnClass}
                  title={`Restrict to '${name}' attribute${key}`}
                  active={tokenSelected.column === colPath}
                  onClick={ update({column: colPath}) }>
                  {name}
                  <div className='column-type'>{typeString}</div>
                </Button>
              );
            })
          }
        </div>
      </>
    </DataElement>
  );
}


// SearchValues component for boolean columns
function SearchValues({ tokenSelected, filter, update, cgSearch }) {
  const colPath = tokenSelected.column;
  const columnPathType = cgSearch.columnPathTypes.get(colPath);
  const value = tokenSelected.value?.toLowerCase?.();
  const trueActive = value === 'true';
  const falseActive = value === 'false';

  const classes = classNames('search-value-section', {
    'search-value-section-open': columnPathType === 'boolean'
  });

  return (
    <DataElement className={classes}>
      <ButtonGroup>
        <Button active={trueActive} width='calc(50% + 1px)' onClick={update({ value: 'true' })}>True</Button>
        <Button active={falseActive} width='50%' onClick={update({ value: 'false' })}>False</Button>
      </ButtonGroup>
    </DataElement>
  );
}


